/**
 * Bill Validation — which retailer-invoice figures are the real ones.
 *
 * A bill carries two versions of the retailer invoice:
 *
 *   actual_parsed_result   what the parser extracted. Immutable — only
 *                          `parseInvoicePdf` writes it.
 *   actual_edited_result   what a person corrected against the PDF. NULL until
 *                          somebody edits.
 *
 * The corrected one wins where it exists. This module is the ONLY place that
 * decision is made — deliberately, because getting it wrong is silent. A
 * consumer that forgets the fallback does not throw; it shows, or emails to a
 * paying client, the number nobody corrected.
 *
 * After this landed, the only files allowed to name `actual_parsed_result` are
 * `invoiceParserService.ts` (writes it) and this one (reads it). Everything else
 * goes through `effectiveInvoice`. That grep is the test.
 *
 * Dependency-free — no React, no Supabase — so the server imports it as-is, the
 * same way it already imports `reportFormulas.ts`.
 */

import type { BvBill, ActualBillResult, ActualBillLine } from '../types/billTypes';

export type InvoiceSource = 'parsed' | 'edited';

export type EffectiveInvoice = {
  /** The figures to use. Null when the invoice has not been parsed at all. */
  result: ActualBillResult | null;
  /** Where they came from. Null when there is nothing yet. */
  source: InvoiceSource | null;
  editedAt: string | null;
  editedBy: string | null;
  /** Why it was corrected, if whoever corrected it said. Optional by design. */
  editNote: string | null;
  /** The untouched parse, for showing "was 206.29" beside a correction. */
  parsed: ActualBillResult | null;
  /** Indexes into `result.lines` whose amount differs from the parse. */
  changedLineIndexes: number[];
};

const EMPTY: EffectiveInvoice = {
  result: null, source: null, editedAt: null, editedBy: null, editNote: null,
  parsed: null, changedLineIndexes: [],
};

/**
 * Whether an edited result counts as "there".
 *
 * NULL is the only empty. An edited result with `lines: []` is somebody having
 * deleted every line — odd, but a real edit, and honouring it is the difference
 * between "I cleared this deliberately" and the system quietly restoring what
 * they cleared.
 */
function hasEdit(edited: ActualBillResult | null | undefined): edited is ActualBillResult {
  return !!edited && Array.isArray(edited.lines);
}

/** Lines that differ from the parse — by amount, description or section. */
function diffLines(
  edited: ActualBillLine[] | undefined,
  parsed: ActualBillLine[] | undefined,
): number[] {
  if (!edited) return [];
  if (!parsed) return edited.map((_, i) => i);

  const out: number[] = [];
  for (let i = 0; i < edited.length; i++) {
    const e = edited[i];
    const p = parsed[i];
    // Beyond the end of the parse, or materially different at the same position.
    // Positional comparison is imperfect once a line is inserted or removed —
    // it over-reports rather than under-reports, which is the right way round
    // for something whose only job is to draw attention.
    if (!p
      || e.section !== p.section
      || e.description !== p.description
      || Math.abs((e.amount ?? 0) - (p.amount ?? 0)) > 0.005) {
      out.push(i);
    }
  }
  return out;
}

/**
 * The figures to use for this bill, and where they came from.
 *
 * Accepts the loose shape rather than a strict `BvBill` so a partial row from a
 * narrow `select` still resolves — several list screens fetch only a few columns.
 */
export function effectiveInvoice(bill: Partial<BvBill> | null | undefined): EffectiveInvoice {
  if (!bill) return EMPTY;

  const parsed = (bill.actual_parsed_result as ActualBillResult | null | undefined) ?? null;
  const edited = (bill.actual_edited_result as ActualBillResult | null | undefined) ?? null;

  if (hasEdit(edited)) {
    return {
      result: edited,
      source: 'edited',
      editedAt: bill.actual_edited_at ?? null,
      editedBy: bill.actual_edited_by ?? null,
      editNote: bill.actual_edited_note?.trim() || null,
      parsed,
      changedLineIndexes: diffLines(edited.lines, parsed?.lines),
    };
  }

  if (parsed) {
    return {
      result: parsed, source: 'parsed', editedAt: null, editedBy: null,
      editNote: null, parsed, changedLineIndexes: [],
    };
  }

  return EMPTY;
}

/** Shorthand for the common case — the figures, or null. */
export function effectiveInvoiceResult(
  bill: Partial<BvBill> | null | undefined,
): ActualBillResult | null {
  return effectiveInvoice(bill).result;
}

/** True when a person has corrected this invoice by hand. */
export function isInvoiceEdited(bill: Partial<BvBill> | null | undefined): boolean {
  return effectiveInvoice(bill).source === 'edited';
}

/**
 * A short line for the UI: "Corrected by hand — 3 lines changed, 12 Aug 2026".
 * Null when nothing has been corrected, so callers can render it unconditionally.
 */
export function editedSummary(bill: Partial<BvBill> | null | undefined): string | null {
  const { source, changedLineIndexes, editedAt } = effectiveInvoice(bill);
  if (source !== 'edited') return null;

  const n = changedLineIndexes.length;
  const when = editedAt
    ? new Date(editedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  const changed = n === 0 ? 'totals adjusted' : `${n} line${n === 1 ? '' : 's'} changed`;
  return when ? `Corrected by hand — ${changed}, ${when}` : `Corrected by hand — ${changed}`;
}
